import { useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import BarChartIcon from '@mui/icons-material/BarChart';
import AddIcon from '@mui/icons-material/Add';
import {
    gridFilteredSortedRowIdsSelector,
    gridSortedRowIdsSelector,
    GridToolbarColumnsButton,
    GridToolbarContainer, GridToolbarDensitySelector,
    useGridApiContext,
} from '@mui/x-data-grid';
import { Box, createSvgIcon } from '@mui/material';

export function CoralToolBar({
    toolBarId, charts, setChart, compounds, addCompound, exportOptions,
}) {
    const compoundOptions = compounds.map(
        (comp) => ({ title: comp.resSpec, onClick: () => addCompound(comp) })
    );

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
            padding: '4px 4px 0px',
            alignItems: 'center',
            border: '1px solid lightgray',
        }}
        >
            <AddCompoundButton toolBarId={toolBarId} compoundOptions={compoundOptions} />
            {!!exportOptions && (
                <ExportButton toolBarId={toolBarId} exportOptions={exportOptions} />
            )}
            <ChartButton toolBarId={toolBarId} charts={charts} setActiveChart={setChart} />
        </Box>
    );
}
export function GridToolBar({
    exportFileName, toolBarId, charts, setChart, ...toolBarProps
}) {
    const fileName = exportFileName || `BMaps Table ${new Date().toLocaleDateString('en-US')}`;
    const ref = useGridApiContext();
    const exportOptions = [
        {
            title: 'Export All to CSV',
            handleExport: () => ref.current.exportDataAsCsv({
                allColumns: true,
                getRowsToExport: ({ apiRef }) => gridSortedRowIdsSelector(apiRef),
                fileName,
                includeColumnGroupsHeaders: false,
            }),
        },
        {
            title: 'Export Visible to CSV',
            handleExport: () => ref.current.exportDataAsCsv({
                getRowsToExport: ({ apiRef }) => gridFilteredSortedRowIdsSelector(apiRef),
                fileName,
                includeColumnGroupsHeaders: false,
            }),
        },
    ];
    return (
        <GridToolbarContainer {...toolBarProps}>
            <GridToolbarColumnsButton />
            <GridToolbarDensitySelector />
            <ExportButton toolBarId={toolBarId} exportOptions={exportOptions} />
            {charts && charts.length > 1 && (
                <ChartButton toolBarId={toolBarId} charts={charts} setActiveChart={setChart} />
            )}
        </GridToolbarContainer>
    );
}

function ExportButton({ toolBarId, exportOptions }) {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const open = Boolean(anchorEl);

    const ExportIcon = createSvgIcon(
        <path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z" />,
        'SaveAlt',
    );

    return (
        <>
            <Button
                color="primary"
                size="small"
                startIcon={<ExportIcon />}
                aria-describedby={`${toolBarId}-export`}
                onClick={handleClick}
            >
                Export
            </Button>
            <Menu
                id={`${toolBarId}-export`}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                {exportOptions.map((option) => (
                    <MenuItem
                        key={option.title}
                        onClick={() => {
                            handleClose();
                            option.handleExport();
                        }}
                    >
                        {option.title}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

function ChartButton({ toolBarId, charts, setActiveChart }) {
    const [chartAnchorEl, setChartAnchorEl] = useState(null);

    const handleCharts = (event) => {
        setChartAnchorEl(event.currentTarget);
    };

    const handleChartClose = () => {
        setChartAnchorEl(null);
    };

    const openChart = Boolean(chartAnchorEl);
    return (
        <>
            <Button
                color="primary"
                size="small"
                startIcon={<BarChartIcon />}
                aria-describedby={`${toolBarId}-charts`}
                onClick={handleCharts}
            >
                Charts
            </Button>
            <Menu
                id={`${toolBarId}-charts`}
                anchorEl={chartAnchorEl}
                open={openChart}
                onClose={handleChartClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                {charts.map((chart) => (
                    <MenuItem
                        key={chart.id}
                        onClick={() => { setActiveChart(chart.id); }}
                    >
                        {chart.name}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
function AddCompoundButton({ compoundOptions, toolBarId }) {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const open = Boolean(anchorEl);
    return (
        <>
            <Button
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                aria-describedby={`${toolBarId}-compound`}
                onClick={handleClick}
            >
                Compound
            </Button>
            <Menu
                id={`${toolBarId}-compound`}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                {compoundOptions.map((option) => (
                    <MenuItem
                        key={option.title}
                        onClick={() => {
                            handleClose();
                            option.onClick();
                        }}
                    >
                        {option.title}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
